import { useCallback, useState } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import * as Types from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

interface IUseDispatcherApi {
  isLoading: boolean
  dispatchers: Types.IDispatcher[]
  error?: string
  getDispatchers: (params?: Types.IFetchDispatcherParams) => Promise<void>
  getDispatcherOptions: (groupOptions: { groupIndex: number }) => IAutocompleteOption[]
  getDispatcherById: (customerId: string) => Types.IDispatcher | undefined
}

/**
 * Hook for fetching/storing dispatcher query data and state
 * @param {string} [fullName] - Filter dispatchers list by full name
 * @param {number} [motorCarrierId] - Filter dispatchers list by motor carrier id
 * @see https://fp-staging.transfix.io/api-docs/index.html
 */
export const useDispatcherApi = (): IUseDispatcherApi => {
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [dispatchers, setDispatchers] = useState<Types.IDispatcher[]>([])

  /**
   * Update customers in local state
   * @param {string} [name] - optional `name` filter value
   */
  const getDispatchers = useCallback(async (params?: Types.IFetchDispatcherParams) => {
    setLoading(true)
    try {
      const { dispatchers: dispatcherList } = await Api.fetchDispatchers(params)
      setDispatchers(dispatcherList)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      setError(e)
    }
  }, [])

  /**
   * Find a customer from local state
   * @param {string} customerId
   */
  const getDispatcherById = useCallback(
    (customerId: string) => {
      return dispatchers.find(({ id }) => customerId === id)
    },
    [dispatchers]
  )

  /**
   * Get list of Autocomplete-compatible options with
   * optional and dynamic `groupIndex` param
   */
  const getDispatcherOptions = useCallback(
    (groupOptions: { groupIndex: number }): IAutocompleteOption[] => {
      return dispatchers.map(({ fullName: text, id }) => ({
        id,
        text,
        ...groupOptions,
      }))
    },
    [dispatchers]
  )

  return {
    isLoading,
    dispatchers,
    error,
    getDispatchers,
    getDispatcherOptions,
    getDispatcherById,
  }
}
